import { createFileRoute } from "@tanstack/react-router";

import { BASE_URL } from "@/common/links";
import { AgenticBrowserHero } from "@/landing/components/AgenticBrowserHero";
import { checkIsWondatwinHost } from "@/landing/lib/constants";
import { PAGE_META } from "@/landing/lib/page-meta";
import {
  LinkedinLandingPage,
  wondatwinLandingHead,
} from "@/routes/_landing.linkedin";

export const Route = createFileRoute("/_landing/")({
  component: LandingPage,
  // The standalone wondatwin.com product serves the LinkedIn landing at its
  // root (same deployment, dispatched by Host). Resolve the host in the loader so
  // SSR and hydration agree — loader data is serialized to the client, so the
  // first client render sees the same value the server rendered with.
  loader: async (): Promise<{ isWondatwin: boolean }> => {
    if (import.meta.env.SSR) {
      const { getRequestHost } = await import("@tanstack/react-start/server");
      const host = getRequestHost({ xForwardedHost: true });
      return { isWondatwin: checkIsWondatwinHost(host.split(":")[0] ?? "") };
    }
    return { isWondatwin: checkIsWondatwinHost(window.location.hostname) };
  },
  head: ({ loaderData }) =>
    loaderData?.isWondatwin
      ? wondatwinLandingHead()
      : {
          meta: [
            { title: PAGE_META["/"].title },
            { name: "description", content: PAGE_META["/"].description },
          ],
          links: [{ rel: "canonical", href: `${BASE_URL}/` }],
          scripts: [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                name: "Wonda",
                applicationCategory: "BusinessApplication",
                operatingSystem: "macOS, Windows, Linux",
                description: PAGE_META["/"].description,
                url: BASE_URL,
              }),
            },
          ],
        },
});

function LandingPage() {
  const { isWondatwin } = Route.useLoaderData();

  if (isWondatwin) {
    return <LinkedinLandingPage isStandalone />;
  }

  // The homepage is the hero alone, one screen with no scroll (the layout
  // drops the footer here).
  return <AgenticBrowserHero />;
}
